import React, { useEffect, useState } from 'react';
import portalService from '../../services/portalService';
import { useLanguage, t } from '../../context/LanguageContext';

function BalanceCard({ title, bal, lang }) {
  if (!bal) return (
    <div className="card">
      <div className="card-body py-3 px-3">
        <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
        <div className="text-muted mt-1" style={{ fontSize: 13 }}>{t('No policy assigned', 'Sin política asignada', lang)}</div>
      </div>
    </div>
  );
  return (
    <div className="card">
      <div className="card-body py-3 px-3">
        <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
        <div className="fw-bold mt-1" style={{ fontSize: 28, color: bal.remaining > 0 ? '#198754' : '#dc3545' }}>
          {bal.remaining} h
        </div>
        <div style={{ fontSize: 12, color: '#6c757d' }}>
          {bal.allocated} {t('allocated', 'asignadas', lang)}
          {bal.carryoverActive > 0 && ` + ${bal.carryoverActive} ${t('carryover', 'transferidas', lang)}`}
          {' · '}{bal.used} {t('used', 'utilizadas', lang)}
        </div>
        {bal.carryoverExpiry && <div style={{ fontSize: 11, color: '#ffc107' }}>{t('Carryover expires', 'Transferencia vence el', lang)} {bal.carryoverExpiry}</div>}
        {bal.unpaidHours > 0 && <div style={{ fontSize: 11, color: '#dc3545' }}>+{bal.unpaidHours} h {t('unpaid overage', 'excedente sin pago', lang)}</div>}
      </div>
    </div>
  );
}

export default function PortalPtoPage() {
  const { lang } = useLanguage();
  const year = new Date().getFullYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'pto', startDate: '', endDate: '', hours: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const STATUS_BADGE = {
    pending:  { cls: 'text-bg-warning text-dark', label: t('Pending', 'Pendiente', lang) },
    approved: { cls: 'text-bg-success',           label: t('Approved', 'Aprobada', lang) },
    denied:   { cls: 'text-bg-danger',            label: t('Denied', 'Rechazada', lang) },
  };

  const TYPE_LABELS = {
    pto:    t('PTO', 'Vacaciones', lang),
    sick:   t('Sick', 'Enfermedad', lang),
    unpaid: t('Unpaid', 'Sin pago', lang),
    other:  t('Other', 'Otro', lang),
  };

  const load = () => {
    setLoading(true);
    portalService.getPto(year).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.startDate || !form.endDate || !form.hours) { setFormError(t('All fields are required.', 'Todos los campos son obligatorios.', lang)); return; }
    setSubmitting(true);
    try {
      await portalService.submitPtoRequest(form);
      setShowForm(false);
      setForm({ type: 'pto', startDate: '', endDate: '', hours: '', note: '' });
      load();
    } catch { setFormError(t('Failed to submit request.', 'Error al enviar la solicitud.', lang)); }
    finally { setSubmitting(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(t('Cancel this request?', '¿Cancelar esta solicitud?', lang))) return;
    await portalService.cancelPtoRequest(id).catch(() => {});
    load();
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;

  const requests = data?.requests || [];
  const adminEntries = data?.adminEntries || [];

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="fw-bold mb-0">{t('Time Off', 'Días libres', lang)}</h5>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(v => !v)}>
          {showForm ? t('Cancel', 'Cancelar', lang) : t('+ Request', '+ Solicitud', lang)}
        </button>
      </div>

      {/* Request form */}
      {showForm && (
        <div className="card mb-3 border-primary">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">{t('New Request', 'Nueva solicitud', lang)}</h6>
            {formError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{formError}</div>}
            <form onSubmit={handleSubmit}>
              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label className="form-label" style={{ fontSize: 12 }}>{t('Type', 'Tipo', lang)}</label>
                  <select className="form-select form-select-sm" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="pto">{t('PTO', 'Vacaciones', lang)}</option>
                    <option value="sick">{t('Sick', 'Enfermedad', lang)}</option>
                    <option value="unpaid">{t('Unpaid', 'Sin pago', lang)}</option>
                    <option value="other">{t('Other', 'Otro', lang)}</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label" style={{ fontSize: 12 }}>{t('Hours', 'Horas', lang)}</label>
                  <input type="number" min="0.5" step="0.5" className="form-control form-control-sm" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} />
                </div>
                <div className="col-6">
                  <label className="form-label" style={{ fontSize: 12 }}>{t('Start Date', 'Fecha de inicio', lang)}</label>
                  <input type="date" className="form-control form-control-sm" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
                </div>
                <div className="col-6">
                  <label className="form-label" style={{ fontSize: 12 }}>{t('End Date', 'Fecha de fin', lang)}</label>
                  <input type="date" className="form-control form-control-sm" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label" style={{ fontSize: 12 }}>{t('Note (optional)', 'Nota (opcional)', lang)}</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                </div>
              </div>
              <button className="btn btn-primary btn-sm w-100" disabled={submitting}>
                {submitting ? t('Submitting…', 'Enviando…', lang) : t('Submit Request', 'Enviar solicitud', lang)}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Balances */}
      <div className="row g-2 mb-3">
        <div className="col-6"><BalanceCard title={t('PTO', 'Vacaciones', lang)} bal={data?.ptoBal} lang={lang} /></div>
        <div className="col-6"><BalanceCard title={t('Sick', 'Enfermedad', lang)} bal={data?.sickBal} lang={lang} /></div>
      </div>

      {/* My requests */}
      <h6 className="fw-semibold mb-2">{t('My Requests', 'Mis solicitudes', lang)}</h6>
      {requests.length === 0 && <p className="text-muted" style={{ fontSize: 13 }}>{t('No requests yet.', 'Sin solicitudes por ahora.', lang)}</p>}
      {requests.map(r => (
        <div key={r.id} className="card mb-2">
          <div className="card-body py-2 px-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <span className="fw-semibold" style={{ fontSize: 13 }}>{TYPE_LABELS[r.type]} · {r.startDate === r.endDate ? r.startDate : `${r.startDate} – ${r.endDate}`}</span>
                <div className="text-muted" style={{ fontSize: 12 }}>{r.hours} h{r.note ? ` · ${r.note}` : ''}</div>
                {r.adminNote && <div style={{ fontSize: 12, color: r.status === 'denied' ? '#dc3545' : '#6c757d', marginTop: 2 }}>Admin: {r.adminNote}</div>}
              </div>
              <div className="d-flex flex-column align-items-end gap-1">
                <span className={`badge ${STATUS_BADGE[r.status]?.cls}`} style={{ fontSize: 11 }}>{STATUS_BADGE[r.status]?.label}</span>
                {r.status === 'pending' && (
                  <button className="btn btn-link btn-sm p-0 text-danger" style={{ fontSize: 11 }} onClick={() => handleCancel(r.id)}>{t('Cancel', 'Cancelar', lang)}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Admin-entered entries */}
      {adminEntries.length > 0 && (
        <>
          <h6 className="fw-semibold mb-2 mt-3" style={{ fontSize: 13, color: '#6c757d' }}>{t('Logged by Admin', 'Registrado por el admin', lang)}</h6>
          {adminEntries.map(e => (
            <div key={e.id} className="card mb-2" style={{ borderColor: '#dee2e6' }}>
              <div className="card-body py-2 px-3">
                <span className="fw-semibold" style={{ fontSize: 13 }}>{TYPE_LABELS[e.type]} · {e.date}</span>
                <span className="text-muted ms-2" style={{ fontSize: 12 }}>{e.hours} h{e.note ? ` · ${e.note}` : ''}</span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
